import type { SecurityEvent } from "@/types/dashboard";
import { useCallback, useEffect, useRef, useState } from "react";

export type DemoPhase =
  | "first-impression"
  | "system-comes-alive"
  | "threat-score-move"
  | "explainability"
  | "multi-channel"
  | "observe-only"
  | "high-fidelity"
  | "end";

interface DemoState {
  phase: DemoPhase;
  running: boolean;
  paused: boolean;
  elapsed: number; // seconds
}

const PHASES: { phase: DemoPhase; start: number; end: number }[] = [
  { phase: "first-impression", start: 0, end: 30 },
  { phase: "system-comes-alive", start: 30, end: 180 },
  { phase: "threat-score-move", start: 180, end: 300 },
  { phase: "explainability", start: 300, end: 420 },
  { phase: "multi-channel", start: 420, end: 600 },
  { phase: "observe-only", start: 600, end: 720 },
  { phase: "high-fidelity", start: 720, end: 900 },
  { phase: "end", start: 900, end: 9999 },
];

export function useDemoController() {
  const [state, setState] = useState<DemoState>({
    phase: "first-impression",
    running: false,
    paused: false,
    elapsed: 0,
  });
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const timer = useRef<NodeJS.Timeout | null>(null);

  const getPhase = (elapsed: number): DemoPhase => {
    const found = PHASES.find((p) => elapsed >= p.start && elapsed < p.end);
    return found ? found.phase : "end";
  };

  const start = useCallback(() => {
    setState((s) => ({ ...s, running: true, paused: false }));
  }, []);

  const pause = useCallback(() => {
    setState((s) => ({ ...s, paused: true }));
  }, []);

  const resume = useCallback(() => {
    setState((s) => ({ ...s, paused: false }));
  }, []);

  const reset = useCallback(() => {
    setState({
      phase: "first-impression",
      running: false,
      paused: false,
      elapsed: 0,
    });
    setEvents([]);
  }, []);

  useEffect(() => {
    if (!state.running || state.paused) {
      if (timer.current) clearInterval(timer.current);
      return;
    }
    timer.current = setInterval(() => {
      setState((s) => {
        const nextElapsed = s.elapsed + 1;
        const nextPhase = getPhase(nextElapsed);
        return { ...s, elapsed: nextElapsed, phase: nextPhase };
      });
    }, 1000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [state.running, state.paused]);

  return {
    events,
    setEvents,
    ...state,
    start,
    pause,
    resume,
    reset,
    setElapsed: (elapsed: number) =>
      setState((s) => ({ ...s, elapsed, phase: getPhase(elapsed) })),
  };
}
